import {useFormik} from 'formik'
import {cloneDeep, isEmpty, size} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {KTSVG} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  ConfirmActionSwal,
  InputTextArea,
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../_gori/partials/widgets'
import {UploadMultipleFile} from '../../claims'
import {
  ClaimsConfig,
  OPTION_CLAIMS_REASON,
  OPTION_ITEM_TYPE,
  OPTION_CLAIMS_REASON_FEDEX,
  CLAIM_REASON_VALUES,
  OPTION_CLAIMS_REASON_UPIC,
} from '../core/_const'
import ClaimService from '../core/_requests'
import {CARRIERS} from '../../../../_gori/constants'
import {ReviewClaimsModal} from './ReviewClaimsModal'
import {INSURANCE_TYPE} from '../../orders/core/_const'
import {markTouchedRecursively} from '../utils'

type Props = {
  show: boolean
  dataInit?: any
  handleClose: () => void
  handleCreateClaimsSuccess: () => void
  trackingNumber: string
  onCancelCallback: () => void
  dataShipment: {
    carrier: string
    insurance_type: string | null
  }
}

const CreateClaimsModal: React.FC<Props> = ({
  show,
  handleClose,
  dataInit,
  handleCreateClaimsSuccess,
  trackingNumber,
  onCancelCallback,
  dataShipment,
}) => {
  const intl = useIntl()
  let key = useRef<number>(0)
  const {newCancelToken, isCancel} = useCancelToken()
  const {stringYup, arrayRequiredYup, infoYup, claimYup, numberYup} = UseYupValidate()
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<any>()
  const [dataForm, setDataForm] = useState<any>()
  const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false)
  const [showPreview, setShowPreview] = useState<boolean>(false)

  const isBaeGuardInsurance = useMemo(() => {
    return dataShipment?.insurance_type === INSURANCE_TYPE.BAE_GUARD
  }, [dataShipment?.insurance_type])

  const initValidateSchema = useMemo(() => {
    return {
      claim_reason: stringYup(50, 'CLAIM_REASON'),
      items: Yup.object().shape({
        [key.current]: Yup.object().shape({
          claim_item_name: stringYup(70, 'ITEM_NAME'),
          claim_item_type: stringYup(50, 'ITEM_TYPE'),
          claim_item_specific_item_type: claimYup.specificItemType,
          claim_item_price: numberYup.unitPrice(),
          claim_item_qty: numberYup.quantity(),
          claim_item_desc: stringYup(2000, 'CLAIM_ITEM_DESCRIPTION'),
          photos: arrayRequiredYup('PHOTO_OF_ITEM'),
        }),
      }),
      claim_invoice: arrayRequiredYup('PROOF_OF_VALUE_INVOICE'),
      claim_recipient_phone: infoYup.phone("RECIPIENT'S_PHONE"),
      claim_recipient_email: infoYup.email("RECIPIENT'S_EMAIL", false),
      consignee_email: infoYup.email('CONSIGNEE_EMAIL', isBaeGuardInsurance),
      claim_is_replace_package: Yup.boolean(),
      tracking_number_replace: claimYup.replacementTrackingNumber,
    }
  }, [
    isBaeGuardInsurance,
    stringYup,
    claimYup.specificItemType,
    claimYup.replacementTrackingNumber,
    numberYup,
    arrayRequiredYup,
    infoYup,
  ])
  const [optionClaimsReason, setOptionClaimsReason] = useState(OPTION_CLAIMS_REASON)
  const [validateSchema, setValidateSchema] = useState<any>(initValidateSchema)

  const handleCloseModal = () => {
    handleClose()
    key.current = 0
    formik.resetForm()
    updateClaimReasonOptions()
  }

  useEffect(() => {
    if (isBaeGuardInsurance) {
      setValidateSchema((prev) => {
        return {
          ...prev,
          damaged_description: claimYup.damageDescription,
          consignee_email: infoYup.email('CONSIGNEE_EMAIL', isBaeGuardInsurance),
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBaeGuardInsurance])

  const validationSchema = Yup.object().shape(validateSchema)
  const formik = useFormik({
    initialValues: {
      claim_reason: '',
      damaged_description: '',
      items: {
        0: {
          show: true,
          claim_item_name: '',
          claim_item_specific_item_type: '',
          claim_item_type: '',
          claim_item_price: '',
          claim_item_qty: '',
          claim_item_desc: '',
          photos: [],
        },
      },
      claim_invoice: [],
      claim_is_replace_package: false,
      claim_is_plan_replace_package: false,
      tracking_number_replace: '',
      claim_recipient_phone: '',
      claim_recipient_email: '',
      consignee_email: '',
      ...dataInit,
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setDataForm(values)
      setShowConfirmModal(true)
    },
  })

  const onSubmitPhotoOfItem = useCallback(
    (key, values) => {
      formik.setFieldValue(formik.getFieldProps(`items.${key}.photos`).name, values)
    },
    [formik]
  )

  const onSubmitProofOfValue = useCallback(
    (values) => {
      formik.setFieldValue(formik.getFieldProps('claim_invoice').name, values)
    },
    [formik]
  )

  const handleAddNewItem = () => {
    const newValidate = Yup.object().shape({
      ...validateSchema.items.fields,
      [key.current + 1]: cloneDeep(initValidateSchema.items.fields[key.current]),
    })

    setValidateSchema((prev) => {
      return {
        ...prev,
        items: newValidate,
      }
    })

    const newItem = {
      [key.current + 1]: {
        show: true,
        claim_item_name: '',
        claim_item_specific_item_type: '',
        claim_item_type: '',
        claim_item_price: '',
        claim_item_qty: '',
        claim_item_desc: '',
        photos: [],
      },
    }
    formik.setFieldValue(formik.getFieldProps('items').name, {
      ...formik.getFieldProps('items').value,
      ...newItem,
    })
    key.current += 1
  }

  const deleteItemObj = (obj, keyDelete) => {
    const newObject = Object.entries(obj).reduce((initValue, [keyItem, item]) => {
      if (keyItem !== keyDelete) {
        initValue[keyItem] = item
      }
      return initValue
    }, {})

    return newObject
  }

  const handleDeleteItem = (keyDelete) => {
    const objDelete = deleteItemObj(formik.getFieldProps('items').value, keyDelete)
    formik.setFieldValue(formik.getFieldProps('items').name, objDelete)

    const objValidate = deleteItemObj(validateSchema.items.fields, keyDelete)
    setValidateSchema((prev) => {
      return {
        ...prev,
        items: Yup.object().shape(objValidate),
      }
    })
    formik.setTouched({}, false)
  }

  const handleSubmitForm = async () => {
    setIsLoadingForm(true)
    const _dataForm = cloneDeep(dataForm)
    const items = Object.values(_dataForm.items).map((value: any) => {
      if (value.claim_item_type !== ClaimsConfig.ITEM_TYPE_OTHER) {
        value.claim_item_specific_item_type = ''
      }
      return value
    })
    _dataForm.items = items

    try {
      const res = await ClaimService.createClaims(
        {
          ..._dataForm,
          tracking_number: trackingNumber,
          claim_is_replace_package: _dataForm.claim_is_replace_package,
          claim_is_plan_replace_package: _dataForm.claim_is_plan_replace_package,
          insurance_type: dataShipment?.insurance_type || null,
        },
        {cancelToken: newCancelToken()}
      )
      if (res) {
        toast.success(intl.formatMessage({id: 'CREATED_SUCCESSFULLY'}))
        handleCloseModal()
        onCancelReviewClaims()
        handleCreateClaimsSuccess()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      if (error?.response?.data?.error) {
        setValidationErrors(error?.response)
      } else {
        toast.error(intl.formatMessage({id: 'CREATED_FAILED'}))
      }
    } finally {
      setIsLoadingForm(false)
    }
  }

  useEffect(() => {
    if (!formik.getFieldProps('claim_is_replace_package').value) {
      formik.setFieldValue(formik.getFieldProps('tracking_number_replace').name, '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.getFieldProps('claim_is_replace_package').value])

  const updateClaimReasonOptions = useCallback(() => {
    let combinedReasons = [...OPTION_CLAIMS_REASON]
    if (isBaeGuardInsurance) {
      combinedReasons = [...combinedReasons, ...OPTION_CLAIMS_REASON_UPIC]
    }

    if (dataShipment?.carrier === CARRIERS.FEDEX && !isBaeGuardInsurance) {
      formik.setFieldValue('claim_reason', '')
      combinedReasons = [...combinedReasons, ...OPTION_CLAIMS_REASON_FEDEX]
    }
    setOptionClaimsReason(combinedReasons)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataShipment?.carrier, isBaeGuardInsurance])

  useEffect(() => {
    updateClaimReasonOptions()
  }, [updateClaimReasonOptions])

  const onClickReview = async () => {
    const errors = await formik.validateForm()
    if (!isEmpty(errors)) {
      formik.setTouched(markTouchedRecursively(errors))
      return
    }

    setShowPreview(true)
  }

  const onCancelReviewClaims = () => {
    handleCloseModal()
    onCancelCallback?.()
    setShowPreview(false)
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <ReviewClaimsModal
        show={showPreview}
        setShow={setShowPreview}
        formik={formik}
        handleCreateClaim={formik.handleSubmit}
        onCancelCallback={onCancelReviewClaims}
        trackingNumber={trackingNumber}
        optionClaimsReason={optionClaimsReason}
        mode='create'
        isBaeGuardInsurance={isBaeGuardInsurance}
        isLoadingForm={isLoadingForm}
      />
      <ConfirmActionSwal
        show={showConfirmModal}
        title={intl.formatMessage({id: 'START_A_NEW_CLAIM'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleSubmitForm}
        handleClose={() => setShowConfirmModal(false)}
      />

      <Modal
        id='gori_modal_create_claim'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-xl h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={onCancelCallback}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-2'}>
            <div className={'ms-2'}>
              <i
                className='bi bi-chevron-left text-primary fs-5 ms-5 cursor-pointer'
                onClick={handleCloseModal}
              ></i>
              <span className={'ms-3'}>{intl.formatMessage({id: 'CLAIM_FORM'})}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='vh-75 overflow-auto'>
          {/* Title */}
          <div className='mb-13 text-center'>
            <h1 className='mb-3'>{intl.formatMessage({id: 'FILE_A_CLAIM'})}</h1>
            <div className='text-muted fw-semibold fs-5'>
              {intl.formatMessage({
                id: 'TRACKING_NUMBER',
              })}
              : {trackingNumber}
            </div>
          </div>
          <div className='mb-5'>
            <div className='d-flex'>
              <SelectFormik
                className='col-9'
                labelClassName='col-3 col-form-label'
                label={intl.formatMessage({id: 'CLAIM_REASON'})}
                placeholder={intl.formatMessage(
                  {id: 'SELECT_A_INPUT'},
                  {input: intl.formatMessage({id: 'CLAIM_REASON'})}
                )}
                emptyDefault={false}
                required
                options={optionClaimsReason}
                chevronIndicator
                name='claim_reason'
                formik={formik}
                hasUseIntl={true}
                onChange={() => formik.setFieldValue('damaged_description', '')}
              />
            </div>
          </div>
          {isBaeGuardInsurance && (
            <div className='mb-5'>
              <div className='d-flex'>
                <InputTextFormik
                  className='col-9'
                  labelClassName='col-3 col-form-label'
                  label={intl.formatMessage({id: 'CONSIGNEE_EMAIL'})}
                  placeholder={intl.formatMessage({id: 'CONSIGNEE_EMAIL'})}
                  formik={formik}
                  name={'consignee_email'}
                  required
                />
              </div>
            </div>
          )}
          {formik.values.claim_reason === CLAIM_REASON_VALUES.DAMAGE && isBaeGuardInsurance && (
            <div className='row mb-5'>
              <InputTextArea
                className='col-12'
                inputClassName='h-100px'
                labelClassName='col-form-label'
                label={intl.formatMessage({id: 'DAMAGE_DESCRIPTION'})}
                placeholder={intl.formatMessage({id: 'DAMAGE_DESCRIPTION_PLACEHOLDER'})}
                formik={formik}
                name={'damaged_description'}
                required
              />
            </div>
          )}
          {/* Items Detail */}
          <div className='mt-15 mb-5'>
            <h3>{intl.formatMessage({id: 'ITEM_DETAILS'})}</h3>
          </div>
          {Object.entries(formik.getFieldProps('items').value).map(([key, item], index) => {
            return (
              <div key={key} className='bg-light px-10 pt-5 pb-2 mb-4 rounded'>
                <div className='d-flex align-items-center justify-content-between mb-5'>
                  <span className='fs-6 fw-bolder'>
                    {intl.formatMessage({id: 'ITEM'})} {index + 1}
                  </span>
                  <div className='d-flex align-items-center'>
                    {size(formik.getFieldProps('items').value) > ClaimsConfig.SHOW_DELETE_ITEM && (
                      <div
                        className='pe-3 text-danger fw-bold cursor-pointer text-decoration-underline'
                        onClick={() => handleDeleteItem(key)}
                      >
                        {intl.formatMessage({id: 'DELETE_ITEM'})}
                      </div>
                    )}
                    <div
                      className='btn btn-sm btn-icon btn-white btn-active-light opacity-75 cursor-pointer rounded-circle'
                      onClick={() =>
                        formik.setFieldValue(
                          formik.getFieldProps(`items.${key}.show`).name,
                          !formik.getFieldProps(`items.${key}.show`).value
                        )
                      }
                    >
                      {formik.getFieldProps(`items.${key}.show`).value ? (
                        <KTSVG path='/media/gori/claims/down.svg' className='svg-icon-2' />
                      ) : (
                        <KTSVG path='/media/gori/claims/left.svg' className='svg-icon-2' />
                      )}
                    </div>
                  </div>
                </div>
                {formik.getFieldProps(`items.${key}.show`).value && (
                  <>
                    <div className='row mb-5'>
                      <div className='col-md-6'>
                        <div className='d-flex mb-5'>
                          <InputTextFormik
                            className='col-8'
                            labelClassName='col-4 col-form-label'
                            required
                            label={intl.formatMessage({id: 'ITEM_NAME'})}
                            formik={formik}
                            name={`items.${key}.claim_item_name`}
                          />
                        </div>
                        <div className='d-flex mb-5'>
                          <InputTextFormik
                            type={'number'}
                            min={0}
                            placeholder='$'
                            className='col-8'
                            labelClassName='col-4 col-form-label'
                            required
                            label={intl.formatMessage({id: 'UNIT_PRICE'})}
                            formik={formik}
                            name={`items.${key}.claim_item_price`}
                            isCurrency
                          />
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className='d-flex mb-5'>
                          <SelectFormik
                            className='col-8'
                            labelClassName='col-4 col-form-label'
                            label={intl.formatMessage({id: 'ITEM_TYPE'})}
                            placeholder={intl.formatMessage(
                              {id: 'SELECT_A_INPUT'},
                              {input: intl.formatMessage({id: 'ITEM_TYPE'})}
                            )}
                            emptyDefault={false}
                            required
                            options={OPTION_ITEM_TYPE}
                            chevronIndicator
                            formik={formik}
                            name={`items.${key}.claim_item_type`}
                            hasUseIntl={true}
                          />
                        </div>
                        {formik.getFieldProps(`items.${key}.claim_item_type`).value ===
                          ClaimsConfig.ITEM_TYPE_OTHER && (
                          <div className='d-flex mb-5'>
                            <InputTextFormik
                              className='col-8'
                              labelClassName='col-4 col-form-label'
                              required
                              label={intl.formatMessage({id: 'SPECIFIC_ITEM_TYPE'})}
                              formik={formik}
                              name={`items.${key}.claim_item_specific_item_type`}
                            />
                          </div>
                        )}

                        <div className='d-flex'>
                          <InputTextFormik
                            type={'number'}
                            min={0}
                            isInteger
                            className='col-8'
                            labelClassName='col-4 col-form-label'
                            required
                            label={intl.formatMessage({id: 'QUANTITY'})}
                            formik={formik}
                            name={`items.${key}.claim_item_qty`}
                          />
                        </div>
                      </div>
                    </div>
                    <div className='row mb-5'>
                      <InputTextArea
                        className='col-12'
                        inputClassName='h-100px'
                        labelClassName='col-form-label'
                        label={intl.formatMessage({id: 'CLAIM_ITEM_DESCRIPTION'})}
                        formik={formik}
                        name={`items.${key}.claim_item_desc`}
                        required
                      />
                    </div>
                    <div className='row mb-5'>
                      <UploadMultipleFile
                        label={intl.formatMessage({
                          id:
                            formik.getFieldProps('claim_reason').value ===
                            CLAIM_REASON_VALUES.DAMAGE
                              ? 'PHOTO_OF_DAMAGED_ITEM'
                              : 'PHOTO_OF_ITEM',
                        })}
                        required
                        onSubmit={(value) => onSubmitPhotoOfItem(key, value)}
                        formik={formik}
                        name={`items.${key}.photos`}
                      />
                    </div>
                  </>
                )}
              </div>
            )
          })}
          {size(formik.getFieldProps('items').value) < ClaimsConfig.SHOW_ADD_NEW_ITEM &&
            !isBaeGuardInsurance && (
              <div
                className='btn btn-light d-grid d-flex justify-content-center'
                onClick={handleAddNewItem}
              >
                <KTSVG path={'/media/gori/claims/add-item.svg'} className='svg-icon-3' />
                {intl.formatMessage({id: 'ADD_NEW_ITEM'})}
              </div>
            )}
          {/* Purchase Information */}
          <div className='mt-15 mb-5'>
            <h3>{intl.formatMessage({id: 'PURCHASE_INFORMATION'})}</h3>
          </div>
          <div className='row mb-10'>
            <UploadMultipleFile
              label={intl.formatMessage({id: 'PROOF_OF_VALUE_INVOICE'})}
              required
              onSubmit={onSubmitProofOfValue}
              formik={formik}
              name={'claim_invoice'}
            />
          </div>
          {/* Replacement Package Information */}
          <div className='mt-15 mb-5'>
            <h3>{intl.formatMessage({id: 'REPLACEMENT_PACKAGE_INFORMATION'})}</h3>
          </div>
          <div className='d-flex align-items-center mb-5'>
            <span className='fs-6 fw-semibold text-gray-800 d-block'>
              {intl.formatMessage({id: 'HAS_A_REPLACEMENT_PACKAGE_BEEN_SHIPPED'})}
            </span>
            <label className='form-check form-switch form-check-custom form-check-solid px-5'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={formik.getFieldProps('claim_is_replace_package').value}
                value={formik.getFieldProps('claim_is_replace_package').value}
                onChange={formik.handleChange}
                name='claim_is_replace_package'
              />
            </label>
          </div>
          {formik.getFieldProps('claim_is_replace_package').value ? (
            <div className='d-flex'>
              <InputTextFormik
                className='col-9'
                labelClassName='col-3 col-form-label'
                label={intl.formatMessage({id: 'REPLACEMENT_TRACKING_NUMBER'})}
                required
                formik={formik}
                name={'tracking_number_replace'}
              />
            </div>
          ) : (
            <div className='d-flex align-items-center mb-5'>
              <span className='fs-6 fw-semibold text-gray-800 d-block'>
                {intl.formatMessage({id: 'DO_YOU_PLAN_ON_SHIPPING_A_RE_PLACEMENT_PACKAGE'})}
              </span>
              <label className='form-check form-switch form-check-custom form-check-solid px-5'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={formik.getFieldProps('claim_is_plan_replace_package').value}
                  value={formik.getFieldProps('claim_is_plan_replace_package').value}
                  onChange={formik.handleChange}
                  name='claim_is_plan_replace_package'
                />
              </label>
            </div>
          )}
          {/* Recipient Contacts */}
          <div className='mt-15 mb-5'>
            <h3>{intl.formatMessage({id: 'RECIPIENT_CONTACTS'})}</h3>
          </div>
          <div className='row mb-5'>
            <div className='col-md-6 mb-1'>
              <div className='d-flex'>
                <InputTextFormik
                  className='col-8'
                  labelClassName='col-4 col-form-label'
                  required
                  label={intl.formatMessage({id: "RECIPIENT'S_PHONE"})}
                  formik={formik}
                  name='claim_recipient_phone'
                />
              </div>
            </div>
            <div className='col-md-6'>
              <div className='d-flex'>
                <InputTextFormik
                  className='col-8'
                  labelClassName='col-4 col-form-label'
                  label={intl.formatMessage({id: "RECIPIENT'S_EMAIL"})}
                  formik={formik}
                  name='claim_recipient_email'
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-2'
              label={intl.formatMessage({id: 'CANCEL'})}
              loadingText={intl.formatMessage({id: 'CANCEL'})}
              event={() => {
                handleCloseModal()
                onCancelCallback()
              }}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'REVIEW'})}
              loadingText={intl.formatMessage({id: 'REVIEW'})}
              disabled={isLoadingForm}
              event={onClickReview}
              loading={isLoadingForm}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {CreateClaimsModal}
